/**
 * galaxy-arrival-warnings.ts — v4M12.1 defender arrival-angle preview.
 *
 * Per-frame galaxy-view pass that draws empire-coloured inward
 * chevrons on each destination system's Jump Area ring for inbound
 * enemy jumps visible to the viewer under the Jump Prediction
 * Sensors tech. All visibility gating lives in the sim-side helper
 * `incomingJumpArrivalsForViewer` (src/game/jump-prediction.ts) per
 * the v4M12.0 design lock in `docs/TECHTREE.md §9.1`.
 *
 * Distinct from `galaxy-jump-target.ts`:
 *   - That module is the SELECTION-driven destination picker —
 *     visible only while the player holds a fleet selected and hovers
 *     over a candidate destination. It draws the Jump Area ring
 *     outline plus the player's own arrival marker at the cursor
 *     projection.
 *   - This module is the TECH-driven defender warning — visible
 *     whenever an enemy is inbound with a non-null angle and the
 *     defender meets the visibility gate, regardless of selection.
 *     Renders chevrons WITHOUT redrawing the ring outline so the
 *     surface stays unobtrusive when the picker isn't also active
 *     (the chevron sits at the same `starRadius × 2.2` radius the
 *     picker uses, so when both are active the markers coincide
 *     with the picker ring naturally).
 *
 * v4M12.2 will land the system-view counterpart on the in-system
 * Jump Area at r=0.48.
 */

import { incomingJumpArrivalsForViewer } from '../game/jump-prediction';
import { JUMP_PREDICTION_TECH } from '../game/jump-prediction';
import { drawArrivalMarker } from './galaxy-jump-target';
import type { GameState } from '../state/types/index';
import type { UiState } from '../ui/state';
import { viewerEmpireId } from '../ui/state';
import { worldToScreen, type Viewport } from './camera';

/**
 * Top-level driver. Called once per frame from `galaxy-view.ts`. No-op
 * when the viewer hasn't researched the tech (early-out before the
 * per-system loop saves the per-system scanner walk on every frame in
 * pre-tech sessions).
 *
 * `starRadius` matches the value `galaxy-view.ts` uses for the per-
 * star draw + the picker ring radius — keep them in lockstep so the
 * defender chevron lands on the same imaginary ring the picker uses.
 */
export function drawGalaxyArrivalWarnings(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  starRadius: number,
): void {
  if (ui.viewMode !== 'galaxy') return;
  const viewerId = viewerEmpireId(state, ui);
  const viewer = state.empires.get(viewerId);
  if (!viewer) return;
  // Early-out: skip the per-system loop when the tech isn't researched.
  // The helper enforces this gate too, but checking once here saves the
  // per-system scanner walk on every frame pre-research.
  if (!viewer.researchedTechs.has(JUMP_PREDICTION_TECH)) return;

  const ringRadius = starRadius * 2.2;
  for (const system of state.systems.values()) {
    const arrivals = incomingJumpArrivalsForViewer(state, viewerId, system.id);
    if (arrivals.length === 0) continue;

    const starScreen = worldToScreen(ui.galaxyCamera, vp, {
      x: system.x,
      y: system.y,
    });

    for (const arrival of arrivals) {
      const attacker = state.empires.get(arrival.attackerEmpireId);
      // Attacker empire missing is a corrupt-state edge — fall back
      // to a neutral red so the chevron still telegraphs "danger".
      const color = attacker?.color ?? '#ff5555';
      // Synthesize a RingProjection at the arrival angle. The
      // `drawArrivalMarker` glyph is the same chevron the picker
      // uses for the player's own arrival marker — reusing it keeps
      // "this is where a ship is going to emerge" reading consistent
      // across attacker and defender perspectives.
      const proj = {
        angle: arrival.arrivalAngle,
        x: starScreen.x + Math.cos(arrival.arrivalAngle) * ringRadius,
        y: starScreen.y + Math.sin(arrival.arrivalAngle) * ringRadius,
        isCenterDeadzone: false,
      };
      drawArrivalMarker(ctx, starScreen, proj, color);
    }
  }
}
